/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { PlayerState } from "../videoplayer/enums";

/**
 * The Video Debug Overlay data model: its enumerations, the per-group
 * metric shapes, the aggregate snapshot, the display projection, and the
 * one contract that carries all of it across platforms.
 *
 * Ordered as the overlay renders — enums first, since the shapes below
 * refer to them, then the per-group shapes, then {@link TDebugMetrics}
 * that aggregates them, then the rendered rows, then
 * {@link IVideoMetricsProvider}.
 *
 * The enums are the file's only compile-to-runtime declarations;
 * everything else is erased. It imports nothing but a type, so it stays
 * a leaf — a consumer that needs one enum does not pull the metrics
 * provider, and the player stack behind it, into its module graph.
 */

// ── DebugOverlayMode ────────────────────────────────────────────────────

/**
 * How much of the metric set the overlay renders.
 *
 * Compact is the default and is deliberately small — around ten fields,
 * enough to classify a playback fault (bitrate / buffer / DRM / codec) at a
 * glance without covering the picture. Expanded fills the screen and adds
 * the rest, including the full ABR ladder.
 */
export enum DebugOverlayMode {
  /** ~10 headline fields. The default when the overlay is first shown. */
  COMPACT = "COMPACT",

  /** Every field, including the ABR ladder and device environment. */
  EXPANDED = "EXPANDED"
}

// ── DebugPlaybackType ───────────────────────────────────────────────────

/**
 * The stream classification shown in the overlay's Identity group.
 *
 * Distinct from `PlaybackContentType`, which drives real playback
 * behaviour: this one exists only so a screenshot of the overlay says what
 * kind of stream was on screen, and it collapses the distinctions the
 * controller draws but a diagnostician does not need.
 */
export enum DebugPlaybackType {
  /** Time-shifted playback of a programme already broadcast. */
  CATCH_UP = "Catch Up",

  /** Live linear broadcast. */
  LIVE = "Live",

  /** Network DVR recording. */
  NDVR = "nDVR",

  /** Start Over / SOCU playback of an in-progress airing. */
  START_OVER = "Start Over",

  /** Nothing is loaded, or the type could not be resolved. */
  UNKNOWN = "Unknown",

  /** Video on demand. */
  VOD = "VOD"
}

// ── DebugBufferingSubState ──────────────────────────────────────────────

/**
 * Why the player is buffering.
 *
 * The distinction matters because the two have opposite diagnoses: a player
 * filling its buffer ahead of playback is healthy, whereas one that has run
 * the buffer to empty is about to stall. The top-level player state cannot
 * tell them apart — both report BUFFERING.
 */
export enum DebugBufferingSubState {
  /** Buffering with media still ahead of the playhead — proactive fill. */
  NORMAL = "Normal",

  /** Buffering with an empty buffer — playback has run dry. */
  STARVED = "Starved"
}

// ── DebugMetricGroupId ──────────────────────────────────────────────────

/**
 * The metric groups the overlay renders, in the order it renders them.
 *
 * Mirrors the grouping in the Video Debug Overlay proposal §3 so the Web
 * overlay reads the same way as the Android and iOS ones.
 */
export enum DebugMetricGroupId {
  /** Content / asset identity and stream type. */
  IDENTITY = "IDENTITY",

  /** Resolution, framerate and video codec. */
  VIDEO = "VIDEO",

  /** Audio codec, track and volume. */
  AUDIO = "AUDIO",

  /** Bitrate, throughput and ABR dwell time. */
  BANDWIDTH = "BANDWIDTH",

  /** Buffer ahead of the playhead. */
  BUFFER = "BUFFER",

  /** Decode health — dropped, decoded and corrupted frames. */
  FRAMES = "FRAMES",

  /** Content protection scheme and key state. */
  DRM = "DRM",

  /** The full adaptive bitrate ladder. */
  ABR = "ABR",

  /** Stream source — manifest URI, edge host, filmstrip template. */
  SOURCE = "SOURCE",

  /** Top-level player state and buffering sub-state. */
  STATE = "STATE",

  /** Device and environment context. */
  DEVICE = "DEVICE"
}

// ── TIdentityMetrics ────────────────────────────────────────────────────

/**
 * What is playing. The first thing a bug report needs, and the one group
 * that is useless if it arrives late — so every field falls back to `null`
 * rather than waiting for metadata to settle.
 */
export interface TIdentityMetrics {
  /** Call sign of the tuned channel, or null for non-linear playback. */
  channelCallSign: string | null;

  /** Channel number of the tuned channel, or null for non-linear playback. */
  channelNumber: string | null;

  /** Content / asset identifier, or null when unresolved. */
  contentId: string | null;

  /** Stream classification (Live, VOD, nDVR, …). */
  playbackType: DebugPlaybackType;
}

// ── TVideoMetrics ───────────────────────────────────────────────────────

/**
 * Video rendition health.
 *
 * `optimalWidth` / `optimalHeight` describe the best rendition the player
 * could sustain at the currently estimated throughput, which is what turns
 * "we are at 720p" into an actionable statement: matching the active
 * resolution means ABR is doing its job, and a large gap points at a device
 * or ABR-configuration limit rather than the network.
 */
export interface TVideoMetrics {
  /** Active video codec string (e.g. 'avc1.4d401f'), or null. */
  codec: string | null;

  /** Active video framerate in frames per second, or null. */
  frameRate: number | null;

  /** Active rendition height in pixels, or null. */
  height: number | null;

  /** Height of the highest rendition sustainable at current throughput. */
  optimalHeight: number | null;

  /** Width of the highest rendition sustainable at current throughput. */
  optimalWidth: number | null;

  /** Active rendition width in pixels, or null. */
  width: number | null;
}

// ── TAudioMetrics ───────────────────────────────────────────────────────

/**
 * Audio pipeline state.
 *
 * Normalized loudness (LUFS) from the proposal's reference table is absent
 * on purpose: no browser media API reports it, and a fabricated value in a
 * diagnostic panel is worse than an absent one.
 */
export interface TAudioMetrics {
  /** Channel count of the active track (2 = stereo, 6 = 5.1), or null. */
  channelsCount: number | null;

  /** Active audio codec string (e.g. 'mp4a.40.2'), or null. */
  codec: string | null;

  /** Whether output is muted. */
  isMuted: boolean;

  /** Human-readable label of the active audio track, or null. */
  label: string | null;

  /** BCP 47 language tag of the active audio track, or null. */
  language: string | null;

  /** Output volume from 0 (silent) to 1 (maximum), or null. */
  volume: number | null;
}

// ── TBandwidthMetrics ───────────────────────────────────────────────────

/**
 * Bitrate selection and the network capacity it was selected against.
 */
export interface TBandwidthMetrics {
  /** Bitrate of the active variant in bits per second, or null. */
  bitrateBps: number | null;

  /**
   * Seconds the player has held the current variant. Frequent resets point
   * at an unstable connection; a long dwell at a low rung points at a
   * sustained ceiling.
   */
  secondsOnCurrentBitrate: number | null;

  /** Player's estimate of available bandwidth in bits per second. */
  throughputBps: number | null;
}

// ── TBufferMetrics ──────────────────────────────────────────────────────

/**
 * How much media is ready to play.
 *
 * The byte figure is an estimate, not a measurement: Media Source Extensions
 * exposes buffered *time* ranges and no byte-level occupancy, so it is
 * derived as `aheadSeconds × bitrate / 8`. It is kept because it is the
 * figure that makes buffer pressure comparable across renditions, and the
 * renderer marks it approximate so nobody reads it as ground truth.
 */
export interface TBufferMetrics {
  /** Estimated bytes buffered ahead of the playhead, or null. */
  aheadBytesEstimate: number | null;

  /** Seconds of contiguous media buffered ahead of the playhead, or null. */
  aheadSeconds: number | null;
}

// ── TFrameMetrics ───────────────────────────────────────────────────────

/**
 * Decode health. A dropped-to-decoded ratio that climbs during playback
 * means the device cannot keep up with the rendition it has been given.
 */
export interface TFrameMetrics {
  /** Frames the decoder flagged as corrupted, or null when unreported. */
  corrupted: number | null;

  /** Total frames decoded, or null when unreported by the browser. */
  decoded: number | null;

  /** Total frames dropped, or null when unreported by the browser. */
  dropped: number | null;
}

// ── TAbrVariant ─────────────────────────────────────────────────────────

/**
 * One rung of the adaptive bitrate ladder as the overlay presents it.
 *
 * A projection of the player's variant track, not the track itself: the
 * overlay needs five fields and a selected flag, and depending on the
 * player's own descriptor would drag the whole Shaka track shape into the
 * data model.
 */
export interface TAbrVariant {
  /** Variant bandwidth in bits per second. */
  bandwidthBps: number;

  /** Video framerate of this rung, or null. */
  frameRate: number | null;

  /** Video height in pixels, or null for audio-only variants. */
  height: number | null;

  /** Player-assigned variant id. */
  id: number;

  /** Whether this is the rung the player is currently on. */
  isSelected: boolean;

  /** Video width in pixels, or null for audio-only variants. */
  width: number | null;
}

// ── TAbrMetrics ─────────────────────────────────────────────────────────

/**
 * The full adaptive bitrate ladder offered by the manifest, with the active
 * rung marked. Shown only in expanded mode — it is the answer to "why did
 * the player choose this quality", which is a question you ask second.
 */
export interface TAbrMetrics {
  /** Id of the active variant, or null when none is active. */
  selectedVariantId: number | null;

  /** Every variant in the manifest, in the order the player reports them. */
  variants: ReadonlyArray<TAbrVariant>;
}

// ── TDrmMetrics ─────────────────────────────────────────────────────────

/**
 * Content protection state.
 */
export interface TDrmMetrics {
  /**
   * Whether this platform can answer the hardware-acceleration question
   * at all.
   *
   * The answer comes from `MediaCapabilities.decodingInfo()`, which is
   * Chrome 66+ / Safari 13+ and absent on older Chromium — including the
   * Chromium 52 baseline this app still supports. Where the API is
   * missing there is no second source worth guessing from, so the
   * overlay omits the row entirely rather than showing a permanently
   * blank one: a field that is never populated teaches a tester to
   * ignore it.
   */
  isHardwareAccelerationDetectable: boolean;

  /**
   * Whether the platform reports the active rendition as decoding in a
   * power-efficient (hardware) path.
   *
   * Null while the probe is in flight, and whenever
   * {@link isHardwareAccelerationDetectable} is false.
   */
  isHardwareAccelerated: boolean | null;

  /** EME key system identifier (e.g. 'com.widevine.alpha'), or null. */
  keySystem: string | null;

  /**
   * Distinct `MediaKeyStatus` values across all tracked keys — 'usable',
   * 'expired', 'output-restricted' and so on. Empty when no keys are
   * tracked.
   */
  keyStatuses: ReadonlyArray<string>;

  /**
   * Display name of the active scheme including security level where the
   * platform reports one (e.g. 'Widevine L1'), or null for clear content.
   */
  scheme: string | null;
}

// ── TSourceMetrics ──────────────────────────────────────────────────────

/**
 * Where the stream is coming from.
 */
export interface TSourceMetrics {
  /** Hostname of the edge serving the manifest, or null. */
  cdnHost: string | null;

  /** Resolved manifest URI after redirects and CDN routing, or null. */
  streamingUrl: string | null;

  /**
   * Thumbnail (filmstrip) URL template in use for the current airing, with
   * its `{FILENAME}` placeholder intact, or null when the content resolved
   * none — which is itself the answer to "why is there no filmstrip".
   *
   * Unlike its neighbours this does not come from the player: the template
   * is resolved from the channel's tuning URIs by `videoPlayerUi`, so the
   * ViewModel supplies it.
   */
  thumbnailUrl: string | null;
}

// ── TPlayerStateMetrics ─────────────────────────────────────────────────

/**
 * What the player believes it is doing — which is the first thing to check
 * against what the screen is actually showing.
 */
export interface TPlayerStateMetrics {
  /** Why the player is buffering, or null when it is not buffering. */
  bufferingSubState: DebugBufferingSubState | null;

  /** Top-level player lifecycle state. */
  state: PlayerState;
}

// ── TDeviceMetrics ──────────────────────────────────────────────────────

/**
 * Device and environment context.
 *
 * Memory is only populated where the browser exposes `performance.memory`
 * (Chromium); it is the JavaScript heap, not whole-process footprint, so
 * it tracks leaks rather than absolute use.
 */
export interface TDeviceMetrics {
  /** Application build version, or null. */
  appVersion: string | null;

  /** Unique device identifier, or null when unavailable. */
  deviceId: string | null;

  /**
   * The key a viewer last pressed, as `keyCode (name)` — e.g.
   * `40 (ArrowDown)`. Null until a key has been pressed.
   *
   * The numeric code leads because that is what a TV remote reports and
   * what a platform key-map bug is diagnosed against; the name follows
   * for anyone reading it on a desktop keyboard.
   */
  lastKeyCode: string | null;

  /** Device-local date and time when the snapshot was taken. */
  localTime: string | null;

  /** Total JavaScript heap size in bytes, or null when unreported. */
  memoryTotalBytes: number | null;

  /** Used JavaScript heap size in bytes, or null when unreported. */
  memoryUsedBytes: number | null;

  /** Platform and browser description (e.g. 'Tizen / Chrome 108'). */
  platform: string | null;
}

// ── TDebugMetrics ───────────────────────────────────────────────────────

/**
 * One complete playback-health snapshot — the `MetricsObject` of the Video
 * Debug Overlay proposal §5, and the only thing that crosses the boundary
 * between the per-platform metrics provider and the shared overlay UI.
 *
 * Deliberately free of DOM and player types: it holds numbers, strings and
 * enums, so the privacy filter can rewrite it, a test can build one by
 * hand, and the export path can serialise it without touching a player.
 * Every field is nullable because a snapshot may be taken at any moment,
 * including before a manifest has parsed.
 */
export interface TDebugMetrics {
  /** The adaptive bitrate ladder and the active rung. */
  abr: TAbrMetrics;

  /** Audio codec, track and output level. */
  audio: TAudioMetrics;

  /** Bitrate selection and estimated throughput. */
  bandwidth: TBandwidthMetrics;

  /** Media buffered ahead of the playhead. */
  buffer: TBufferMetrics;

  /** Device and environment context. */
  device: TDeviceMetrics;

  /** Content protection scheme and key state. */
  drm: TDrmMetrics;

  /** Decode health counters. */
  frames: TFrameMetrics;

  /** What is playing. */
  identity: TIdentityMetrics;

  /** Player lifecycle state. */
  playerState: TPlayerStateMetrics;

  /** Stream source — manifest URI, edge host, filmstrip template. */
  source: TSourceMetrics;

  /** Video rendition health. */
  video: TVideoMetrics;
}

// ── TDebugMetricField ───────────────────────────────────────────────────

/**
 * One rendered row of the overlay: a label and an already-formatted value.
 *
 * The projection from {@link TDebugMetrics} to these rows happens in the
 * model layer rather than in the component, so the same rows feed the panel
 * and the clipboard export and the two can never disagree about wording,
 * units or which fields a mode includes.
 */
export interface TDebugMetricField {
  /** Stable identifier, used as the React key and in tests. */
  id: string;

  /**
   * Display label (e.g. 'Bitrate Used'). Not localized — a diagnostic
   * tool read by engineers and QA, never by a viewer.
   */
  label: string;

  /** Formatted value, already carrying its unit. Never empty. */
  value: string;
}

// ── TDebugMetricGroup ───────────────────────────────────────────────────

/**
 * A titled block of rows in the overlay. Groups with no visible rows for
 * the current mode are dropped before rendering, so a
 * group that reaches the component always has at least one field.
 */
export interface TDebugMetricGroup {
  /** Rows in display order. Never empty. */
  fields: ReadonlyArray<TDebugMetricField>;

  /** Which group this is. */
  id: DebugMetricGroupId;

  /** Display heading (e.g. 'Bandwidth'). Not localized. */
  label: string;
}

// ── IVideoMetricsProvider ───────────────────────────────────────────────

/**
 * The seam between the shared overlay and a platform's player SDK.
 *
 * Android, iOS and Web each implement this over their own player; the data
 * model, the polling loop, the privacy filter and the panel are written
 * once against it. Nothing on this contract may throw: a diagnostic tool
 * that can break playback is worse than no diagnostic tool, so an
 * implementation that cannot read a metric reports it as `null`.
 */
export interface IVideoMetricsProvider {
  /**
   * Read every metric the platform can supply right now.
   *
   * Called once per poll tick while the overlay is visible, so it must be
   * cheap and free of side effects on playback.
   *
   * @returns A complete snapshot; unavailable metrics are `null`.
   */
  getSnapshot(): TDebugMetrics;

  /**
   * Whether a source is loaded and metrics are meaningful.
   *
   * @returns True when the player has content loaded.
   */
  isPlaybackActive(): boolean;

  /**
   * Discard state carried between snapshots — currently the bitrate dwell
   * timer. Called when playback stops so a new stream does not inherit the
   * previous one's ABR history.
   */
  reset(): void;
}
